
import { useState, useCallback } from 'react';
import { z } from 'zod';
import { QuestionTemplate } from '@/types/feedback';

// Default questions for candidate-to-recruiter feedback
const candidateToRecruiterQuestions: QuestionTemplate[] = [
  {
    id: 'strengths',
    type: 'textarea',
    label: 'Points forts de l\'entretien',
    placeholder: 'Qu\'avez-vous apprécié lors de l\'entretien avec ce recruteur?',
    required: true
  },
  {
    id: 'improvements',
    type: 'textarea',
    label: 'Points à améliorer',
    placeholder: 'Quels aspects de l\'entretien pourraient être améliorés selon vous?',
    required: true
  },
  {
    id: 'additional_comments',
    type: 'textarea',
    label: 'Commentaires additionnels (optionnel)',
    placeholder: 'Avez-vous d\'autres commentaires à partager?',
    required: false
  }
];

// Default questions for recruiter-to-candidate feedback
const recruiterToCandidateQuestions: QuestionTemplate[] = [
  {
    id: 'strengths',
    type: 'textarea',
    label: 'Points forts du candidat',
    placeholder: 'Quels sont les points forts que vous avez identifiés chez ce candidat?',
    required: true
  },
  {
    id: 'improvements',
    type: 'textarea',
    label: 'Axes d\'amélioration',
    placeholder: 'Quels seraient les axes d\'amélioration pour ce candidat?',
    required: true
  },
  {
    id: 'additional_comments',
    type: 'textarea',
    label: 'Commentaires additionnels (optionnel)',
    placeholder: 'Avez-vous d\'autres commentaires à partager avec le candidat?',
    required: false
  }
];

export const useQuestionsTemplate = () => {
  const [questions, setQuestions] = useState<QuestionTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Generate a schema based on the questions
  const generateSchema = useCallback((questionsList: QuestionTemplate[]) => {
    const schemaFields: Record<string, any> = {};
    
    questionsList.forEach(question => {
      schemaFields[question.id] = question.required 
        ? z.string().min(1, { message: 'Ce champ est requis' })
        : z.string().optional();
    });
    
    return z.object(schemaFields);
  }, []);
  
  // Generate default values for the form
  const generateDefaultValues = useCallback((questionsList: QuestionTemplate[]) => {
    const defaultValues: Record<string, string> = {};
    
    questionsList.forEach(question => {
      defaultValues[question.id] = '';
    });
    
    return defaultValues;
  }, []);
  
  // Function to load questions based on context and direction
  const loadQuestionTemplate = useCallback((context: string, direction: string) => {
    setLoading(true);
    
    console.log(`Loading questions for context: ${context}, direction: ${direction}`);
    
    try {
      // For now, just use our predefined templates based on direction
      // In the future, we could load these from the database based on context as well
      let templateQuestions;
      
      if (direction === 'candidate_to_recruiter') {
        templateQuestions = candidateToRecruiterQuestions;
      } else {
        templateQuestions = recruiterToCandidateQuestions;
      }
      
      setQuestions(templateQuestions);
    } catch (error) {
      console.error("Error loading question template:", error);
      // Fallback to default questions if there's an error
      setQuestions(recruiterToCandidateQuestions);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Generate schema from current questions
  const schema = generateSchema(questions);
  
  return { 
    questions, 
    schema, 
    loadQuestionTemplate,
    generateDefaultValues,
    loading 
  };
};
